/**
 * The help centre.
 *
 * Grouped by category and searchable, because twenty-four articles is already
 * more than a flat list serves, and somebody arriving here is usually looking
 * for one specific thing.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { byCategory, searchArticles, useHelpArticles } from './useHelp';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './Help.module.css';
import { FILTER_MAX } from '../../types/fieldLimits';

export function HelpCenterPage() {
  const { data, isLoading, error } = useHelpArticles();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const matched = searchArticles(data ?? [], query);
    return byCategory(matched);
  }, [data, query]);

  const matchCount = groups.reduce((n, [, items]) => n + items.length, 0);

  if (isLoading) return <Spinner label="Loading help" />;

  if (error) {
    return (
      <PageState title="Could not load help" tone="alert">
        {error instanceof Error ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  return (
    <div className={own.wrap}>
      <h1 className={styles.title}>Help</h1>
      <p className={styles.subtitle}>
        How to do everything in StandingDocs. Every page also has a{' '}
        <strong>?</strong> in the corner that opens the article for that page.
      </p>

      <div className={own.search}>
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.7" />
          <path d="M11 11l3.2 3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
        <label className="vh" htmlFor="help-search">
          Search help articles
        </label>
        <input
          id="help-search"
            maxLength={FILTER_MAX}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help"
        />
      </div>

      {query.trim() && (
        <p className={styles.itemMeta} role="status" aria-live="polite">
          {matchCount} {matchCount === 1 ? 'article' : 'articles'} matching{' '}
          <strong>{query.trim()}</strong>
        </p>
      )}

      {groups.length === 0 ? (
        <p className={styles.state}>
          No article matches <strong>{query.trim()}</strong>. Try a different
          word, or browse the categories by clearing the search.
        </p>
      ) : (
        groups.map(([category, items]) => (
          <section key={category} className={own.category}>
            <h2 className={own.categoryTitle}>{category}</h2>
            <ul className={own.list}>
              {items.map((article) => (
                <li key={article.id} className={own.item}>
                  <Link to={`/help/article/${article.slug}`} className={own.itemLink}>
                    {article.title}
                  </Link>
                  {article.summary && (
                    <p className={own.itemSummary}>{article.summary}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
